
import { ITask } from '../../../data_models/task'
import { EProjectStatus, IProject } from '../../../data_models/project'

import { useEffect, useState } from 'react';
import RichText from '../../common/RichText';
import { FilledButton, OutlinedButton, TextButton } from '../../common/Button';
import RichTextEditor from '../../common/RichTextEditor';
import IconButton from '../../common/IconButton';
import { IoMdClose } from 'react-icons/io';
import { IoMdCheckmark } from 'react-icons/io';
import { FiEdit3 } from 'react-icons/fi';
import { IoPencil } from 'react-icons/io5';
import useProjects from '../../../hooks/useProjects';
import MultiTextSelect from '../../common/MultiTextSelect';
import { Chip } from '../../common/Chip';

export const ProjectDetailsOverview: React.FC<{
    isPm: boolean,
    isEmployer: boolean,
    isRoadmapVerificationPm: boolean,
    isFreelancer: boolean,
    isOutsider: boolean,
    project: IProject,
    setProject: (project: IProject) => void,
}> = ({ isPm, isEmployer, isRoadmapVerificationPm, isFreelancer, isOutsider, project, setProject }) => {


    const { updateProjectDetails } = useProjects();
    const [editDescription, setEditDescription] = useState(false);
    const [newDescription, setNewDescription] = useState(project.description || '');
    const [isSavingDescription, setIsSavingDescription] = useState(false);

    const [editExpectedDelivery, setEditExpectedDelivery] = useState(false);
    const [newExpectedDelivery, setNewExpectedDelivery] = useState(project.expectedDelivery || '');
    const [isSavingExpectedDelivery, setIsSavingExpectedDelivery] = useState(false);

    const [editRequiredSkills, setEditRequiredSkills] = useState(false);
    const [newRequiredSkills, setNewRequiredSkills] = useState(project.requiredSkills || []);
    const [isSavingRequiredSkills, setIsSavingRequiredSkills] = useState(false);

    const [editRequiredTools, setEditRequiredTools] = useState(false);
    const [newRequiredTools, setNewRequiredTools] = useState(project.requiredTools || []);
    const [isSavingRequiredTools, setIsSavingRequiredTools] = useState(false);

    const [editRisks, setEditRisks] = useState(false);
    const [newRisks, setNewRisks] = useState(project.risks || '');
    const [isSavingRisks, setIsSavingRisks] = useState(false);

    const [canEdit, setCanEdit] = useState(false);

    useEffect(() => {
        if ((isPm || isEmployer || isRoadmapVerificationPm) && (project.status || 0) < EProjectStatus.IN_PROGRESS) {
            setCanEdit(true);
        } else {
            setCanEdit(false);
        }

        if (project.requiredSkills) {
            setNewRequiredSkills(project.requiredSkills);
        }

        if (project.requiredTools) {
            setNewRequiredTools(project.requiredTools);
        }
    }, [isPm, isEmployer, project]);


    const onSaveDescription = async () => {
        setIsSavingDescription(true);
        const updateObj: IProject = {
            ...project,
            description: newDescription,
        }
        const updatedProject = await updateProjectDetails(updateObj);
        setProject(updatedProject);
        setEditDescription(false);
        setIsSavingDescription(false);
    }

    const onSaveExpectedDelivery = async () => {
        setIsSavingExpectedDelivery(true);
        const updateObj: IProject = {
            ...project,
            expectedDelivery: newExpectedDelivery,
        }
        const updatedProject = await updateProjectDetails(updateObj);
        setProject(updatedProject);
        setEditExpectedDelivery(false);
        setIsSavingExpectedDelivery(false);
    }

    const onSaveRequiredSkills = async () => {
        setIsSavingRequiredSkills(true);
        const updateObj: IProject = {
            ...project,
            requiredSkills: newRequiredSkills,
        }
        const updatedProject = await updateProjectDetails(updateObj);
        setProject(updatedProject);
        setEditRequiredSkills(false);
        setIsSavingRequiredSkills(false);
    }

    const onSaveRequiredTools = async () => {
        setIsSavingRequiredTools(true);
        const updateObj: IProject = {
            ...project,
            requiredTools: newRequiredTools,
        }
        const updatedProject = await updateProjectDetails(updateObj);
        setProject(updatedProject);
        setEditRequiredTools(false);
        setIsSavingRequiredTools(false);
    }

    const onSaveRisks = async () => {
        setIsSavingRisks(true);
        const updateObj: IProject = {
            ...project,
            risks: newRisks,
        }
        const updatedProject = await updateProjectDetails(updateObj);
        setProject(updatedProject);
        setEditRisks(false);
        setIsSavingRisks(false);
    }


    return (
        <div className="flex flex-col gap-4">


            <div className="-mt-2 -ml-4 flex flex-wrap items-center justify-between sm:flex-nowrap">
                <div className="mt-2 ml-4">
                    <h3 className="text-2xl font-semibold text-gray-900">Overview</h3>
                </div>
            </div>
            <div className="p-4 overflow-hidden rounded-lg bg-white shadow-lg border border-gray-200">
                <dl className="divide-y divide-gray-100">
                    {/* Description */}
                    <div className="px-4 py-6 sm:grid sm:grid-cols-6 sm:gap-4 sm:px-0">
                        <dt className="text-sm/6 font-medium text-gray-900">Description</dt>
                        <dd className={`mt-1 flex text-sm/6 text-gray-700 sm:mt-0 ${canEdit ? 'sm:col-span-4' : 'sm:col-span-5'}`}>
                            <span className="grow">
                                {editDescription
                                    ? <RichTextEditor
                                        value={newDescription}
                                        onChange={(value) => setNewDescription(value)}
                                    />
                                    : <RichText text={project.description || ''} />}
                            </span>
                        </dd>
                        {canEdit && <span className="ml-4 shrink-0">
                            {editDescription
                                ? <div className='flex flex-row justify-end gap-3'>
                                    {/* Save */}
                                    <IconButton
                                        icon={<IoMdCheckmark />}
                                        onClick={onSaveDescription}
                                        theme='primary'
                                        isLoading={isSavingDescription}
                                    />
                                    {/* Cancel */}
                                    <IconButton
                                        icon={<IoMdClose />}
                                        onClick={() => setEditDescription(false)}
                                        theme='danger'

                                    />

                                </div>
                                : <div className='flex flex-col items-end'>
                                    {/* Edit */}
                                    <IconButton
                                        icon={<IoPencil />}
                                        onClick={() => setEditDescription(true)}
                                        theme='primary'
                                        disabled={project._roadmapVerifiedBy !== undefined && project._roadmapVerifiedBy !== null}
                                    />
                                </div>}

                        </span>}
                    </div>

                    {/* Expected Delivery */}
                    <div className="px-4 py-6 sm:grid sm:grid-cols-6 sm:gap-4 sm:px-0">
                        <dt className="text-sm/6 font-medium text-gray-900">Expected Delivery</dt>
                        <dd className={`mt-1 flex text-sm/6 text-gray-700 sm:mt-0 ${canEdit ? 'sm:col-span-4' : 'sm:col-span-5'}`}>
                            <span className="grow">
                                {editExpectedDelivery
                                    ? <RichTextEditor
                                        value={newExpectedDelivery}
                                        onChange={(value) => setNewExpectedDelivery(value)}
                                    />
                                    : <RichText text={project.expectedDelivery || ''} />}
                            </span>
                        </dd>
                        {canEdit && <span className="ml-4 shrink-0">
                            {editExpectedDelivery
                                ? <div className='flex flex-row justify-end gap-3'>
                                    {/* Save */}
                                    <IconButton
                                        icon={<IoMdCheckmark />}
                                        onClick={onSaveExpectedDelivery}
                                        theme='primary'
                                        isLoading={isSavingExpectedDelivery}
                                    />
                                    {/* Cancel */}
                                    <IconButton
                                        icon={<IoMdClose />}
                                        onClick={() => setEditExpectedDelivery(false)}
                                        theme='danger'

                                    />

                                </div>
                                : <div className='flex flex-col items-end'>
                                    {/* Edit */}
                                    <IconButton
                                        icon={<IoPencil />}
                                        onClick={() => setEditExpectedDelivery(true)}
                                        theme='primary'
                                        disabled={project._roadmapVerifiedBy !== undefined && project._roadmapVerifiedBy !== null}
                                    />
                                </div>}

                        </span>}
                    </div>

                    {/* Required Skills */}
                    <div className="px-4 py-6 sm:grid sm:grid-cols-6 sm:gap-4 sm:px-0">
                        <dt className="text-sm/6 font-medium text-gray-900">Required Skills</dt>
                        <dd className={`mt-1 flex text-sm/6 text-gray-700 sm:mt-0 ${canEdit ? 'sm:col-span-4' : 'sm:col-span-5'}`}>
                            <span className="grow">
                                {editRequiredSkills
                                    ? <MultiTextSelect
                                        elements={newRequiredSkills}
                                        setElements={setNewRequiredSkills}
                                        placeholder="Skills"
                                    />
                                    : <div className='flex flex-wrap gap-2'>
                                        {project.requiredSkills?.map((skill) => (
                                            <Chip key={skill}>
                                                {skill}
                                            </Chip>
                                        ))}
                                    </div>}
                            </span>
                        </dd>
                        {canEdit && <span className="ml-4 shrink-0">
                            {editRequiredSkills
                                ? <div className='flex flex-row justify-end gap-3'>
                                    {/* Save */}
                                    <IconButton
                                        icon={<IoMdCheckmark />}
                                        onClick={onSaveRequiredSkills}
                                        theme='primary'
                                        isLoading={isSavingRequiredSkills}
                                    />
                                    {/* Cancel */}
                                    <IconButton
                                        icon={<IoMdClose />}
                                        onClick={() => setEditRequiredSkills(false)}
                                        theme='danger'

                                    />

                                </div>
                                : <div className='flex flex-col items-end'>
                                    {/* Edit */}
                                    <IconButton
                                        icon={<IoPencil />}
                                        onClick={() => setEditRequiredSkills(true)}
                                        theme='primary'
                                        disabled={project._roadmapVerifiedBy !== undefined && project._roadmapVerifiedBy !== null}
                                    />
                                </div>}

                        </span>}
                    </div>


                    {/* Required Tools */}
                    <div className="px-4 py-6 sm:grid sm:grid-cols-6 sm:gap-4 sm:px-0">
                        <dt className="text-sm/6 font-medium text-gray-900">Required Tools</dt>
                        <dd className={`mt-1 flex text-sm/6 text-gray-700 sm:mt-0 ${canEdit ? 'sm:col-span-4' : 'sm:col-span-5'}`}>
                            <span className="grow">
                                {editRequiredTools
                                    ? <MultiTextSelect
                                        elements={newRequiredTools}
                                        setElements={setNewRequiredTools}
                                        placeholder="Tools"
                                        theme='secondary'
                                    />
                                    : <div className='flex flex-wrap gap-2'>
                                        {project.requiredTools?.map((tool) => (
                                            <Chip key={tool} theme='secondary'>
                                                {tool}
                                            </Chip>
                                        ))}
                                    </div>}
                            </span>
                        </dd>
                        {canEdit && <span className="ml-4 shrink-0">
                            {editRequiredTools
                                ? <div className='flex flex-row justify-end gap-3'>
                                    {/* Save */}
                                    <IconButton
                                        icon={<IoMdCheckmark />}
                                        onClick={onSaveRequiredTools}
                                        theme='primary'
                                        isLoading={isSavingRequiredTools}
                                    />
                                    {/* Cancel */}
                                    <IconButton
                                        icon={<IoMdClose />}
                                        onClick={() => setEditRequiredTools(false)}
                                        theme='danger'

                                    />

                                </div>
                                : <div className='flex flex-col items-end'>
                                    {/* Edit */}
                                    <IconButton
                                        icon={<IoPencil />}
                                        onClick={() => setEditRequiredTools(true)}
                                        theme='primary'
                                        disabled={project._roadmapVerifiedBy !== undefined && project._roadmapVerifiedBy !== null}
                                    />
                                </div>}

                        </span>}
                    </div>


                    {/* Risks */}
                    <div className="px-4 py-6 sm:grid sm:grid-cols-6 sm:gap-4 sm:px-0">
                        <dt className="text-sm/6 font-medium text-gray-900">Risks</dt>
                        <dd className={`mt-1 flex text-sm/6 text-gray-700 sm:mt-0 ${canEdit ? 'sm:col-span-4' : 'sm:col-span-5'}`}>
                            <span className="grow">
                                {editRisks
                                    ? <RichTextEditor
                                        value={newRisks}
                                        onChange={(value) => setNewRisks(value)}
                                    />
                                    : <RichText text={project.risks || ''} />}
                            </span>
                        </dd>
                        {canEdit && <span className="ml-4 shrink-0">
                            {editRisks
                                ? <div className='flex flex-row justify-end gap-3'>
                                    {/* Save */}
                                    <IconButton
                                        icon={<IoMdCheckmark />}
                                        onClick={onSaveRisks}
                                        theme='primary'
                                        isLoading={isSavingRisks}
                                    />
                                    {/* Cancel */}
                                    <IconButton
                                        icon={<IoMdClose />}
                                        onClick={() => setEditRisks(false)}
                                        theme='danger'

                                    />

                                </div>
                                : <div className='flex flex-col items-end'>
                                    {/* Edit */}
                                    <IconButton
                                        icon={<IoPencil />}
                                        onClick={() => setEditRisks(true)}
                                        theme='primary'
                                        disabled={project._roadmapVerifiedBy !== undefined && project._roadmapVerifiedBy !== null}
                                    />
                                </div>}

                        </span>}
                    </div>
                </dl>
            </div>

        </div>
    )
}